import { useEffect, useState, useCallback, useMemo } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Plus, ArrowLeft, Calendar, Activity, TrendingDown, Trash2, Archive, ArchiveRestore, AlertTriangle, Flame, Share2, Download } from "lucide-react";
import { toast } from "sonner";
import ClinicalSnapshotEditor from "../components/ClinicalSnapshotEditor";
import PatternSuggestionsPanel from "../components/PatternSuggestionsPanel";
import PhotoTimeline from "../components/PhotoTimeline";
import WhatChangedCard from "../components/clinical/WhatChangedCard";
import PortalShareDialog from "../components/clinical/PortalShareDialog";
import CsvExportDialog from "../components/CsvExportDialog";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [insights, setInsights] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [shareSessionId, setShareSessionId] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [deliveries, setDeliveries] = useState([]);

  const expirationDays = user?.profile?.draft_expiration_days || 30;
  const expirationEnabled = user?.profile?.draft_expiration_enabled !== false;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, i, t, d] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/sessions/by-client/${id}`),
        api.get(`/clients/${id}/insights`),
        api.get(`/clients/${id}/photo-timeline`).catch(() => ({ data: [] })),
        api.get(`/clients/${id}/email-deliveries`).catch(() => ({ data: [] })),
      ]);
      setClient(c.data);
      setSessions(s.data);
      setInsights(i.data);
      setTimeline(t.data || []);
      setDeliveries(d.data || []);
    } catch {
      toast.error("Could not load client");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) load();
    });
    return () => { cancelled = true; };
  }, [load]);

  const saveSnapshot = useCallback(async (patch) => {
    try {
      const r = await api.patch(`/clients/${id}`, patch);
      setClient(r.data);
    } catch {
      toast.error("Could not save");
    }
  }, [id]);

  const restoreSession = async (sid) => {
    try {
      await api.post(`/sessions/${sid}/restore`);
      toast.success("Draft restored");
      load();
    } catch {
      toast.error("Could not restore");
    }
  };

  // Split sessions: active completed | drafts (active) | archived drafts
  const { completedSessions, activeDrafts, archivedDrafts } = useMemo(() => {
    const completed = [];
    const drafts = [];
    const archived = [];
    for (const s of sessions) {
      if (s.archived_at) archived.push(s);
      else if (s.status === "draft") drafts.push(s);
      else completed.push(s);
    }
    return { completedSessions: completed, activeDrafts: drafts, archivedDrafts: archived };
  }, [sessions]);

  // For an active draft, compute days until auto-archive (or null if disabled)
  const daysUntilArchive = useCallback((s) => {
    if (!expirationEnabled) return null;
    const ref = s.updated_at || s.created_at;
    if (!ref) return null;
    const days = expirationDays - (Date.now() - new Date(ref).getTime()) / 86400000;
    return Math.ceil(days);
  }, [expirationDays, expirationEnabled]);

  // Unfinished = drafts untouched for 24h+ (oldest first). Escalation: 3d overdue, 7d critical.
  const unfinishedDrafts = useMemo(() => {
    return activeDrafts
      .filter((s) => Date.now() - new Date(s.updated_at || s.created_at).getTime() > 86400000)
      .map((s) => {
        const ageDays = Math.floor((Date.now() - new Date(s.updated_at || s.created_at).getTime()) / 86400000);
        return { ...s, ageDays, tier: ageDays >= 7 ? "critical" : ageDays >= 3 ? "overdue" : "warning" };
      })
      .sort((a, b) => b.ageDays - a.ageDays);
  }, [activeDrafts]);

  const markDraftComplete = async (sid) => {
    try {
      await api.patch(`/sessions/${sid}`, { status: "completed" });
      toast.success("Session marked complete");
      load();
    } catch {
      toast.error("Could not mark complete");
    }
  };

  const discardDraft = async (sid) => {
    if (!window.confirm("Discard this draft session? Its data will be deleted.")) return;
    try {
      await api.delete(`/sessions/${sid}`);
      toast.success("Draft discarded");
      load();
    } catch {
      toast.error("Could not discard draft");
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (sec < 60) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
    const days = Math.floor(hr / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  };

  const [sessionFilter, setSessionFilter] = useState("all");

  const deleteClient = async () => {
    if (!window.confirm("Delete this client and all their sessions?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Client deleted");
    navigate("/");
  };

  // Archive hides the client from the default list — every record stays intact.
  const toggleArchive = async () => {
    const restoring = !!client.archived;
    try {
      const r = await api.post(`/clients/${id}/${restoring ? "restore" : "archive"}`);
      setClient(r.data);
      toast.success(restoring ? "Client restored — back in your active list" : "Client archived — records and history kept");
    } catch {
      toast.error(restoring ? "Could not restore client" : "Could not archive client");
    }
  };

  if (loading || !client) return <Layout><div className="text-ink-500">Loading…</div></Layout>;

  return (
    <Layout>
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900 mb-8" data-testid="back-to-clients">
        <ArrowLeft className="w-4 h-4" /> All clients
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
        <div className="lg:col-span-2 bg-white border border-paper-200 rounded-3xl p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="label-overline mb-2">Client profile</div>
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <h1 className="font-heading text-4xl font-light tracking-tight text-ink-900">{client.name}</h1>
                {client.archived && (
                  <span data-testid="client-archived-badge" className="text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                    <Archive className="w-3 h-3" strokeWidth={2} /> Archived
                  </span>
                )}
              </div>
              <div className="text-ink-500">
                {[client.age && `${client.age} years`, client.gender].filter(Boolean).join(" · ") || "No demographics recorded"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid={client.archived ? "restore-client-btn" : "archive-client-btn"}
                onClick={toggleArchive}
                title={client.archived ? "Restore client" : "Archive client — keeps all records"}
                className={`min-h-[40px] px-4 rounded-full border text-sm font-medium inline-flex items-center gap-2 transition-all ${
                  client.archived
                    ? "bg-sage-500 hover:bg-sage-600 border-sage-500 text-white"
                    : "bg-white border-paper-200 hover:bg-paper-100 text-ink-700"
                }`}
              >
                {client.archived
                  ? <><ArchiveRestore className="w-4 h-4" strokeWidth={1.5} /> Restore</>
                  : <><Archive className="w-4 h-4" strokeWidth={1.5} /> Archive</>}
              </button>
              <button
                data-testid="delete-client-btn"
                onClick={deleteClient}
                className="w-10 h-10 rounded-full border border-paper-200 hover:bg-paper-100 hover:text-destructive flex items-center justify-center text-ink-500 transition-all"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
          {client.notes && (
            <div className="bg-paper-50 rounded-2xl p-5 border border-paper-200">
              <div className="label-overline mb-2">Intake notes</div>
              <div className="text-ink-700 leading-relaxed">{client.notes}</div>
            </div>
          )}
        </div>

        <div className="bg-sage-500 text-white rounded-3xl p-8 flex flex-col justify-between min-h-[220px]">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] opacity-80 mb-2">Start a session</div>
            <div className="font-heading text-2xl font-light leading-tight">Capture posture, analyze, and treat.</div>
          </div>
          <Link
            to={`/clients/${id}/new-session`}
            data-testid="new-session-btn"
            className="mt-6 min-h-[48px] px-6 rounded-full bg-white text-sage-700 font-medium hover:bg-paper-50 transition-all inline-flex items-center justify-center gap-2 active:scale-95"
          >
            <Plus className="w-4 h-4" /> New session
          </Link>
        </div>
      </div>

      {/* Pattern insights */}
      {insights && insights.total_sessions > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <Stat label="Total sessions" value={insights.total_sessions} icon={<Calendar className="w-4 h-4" />} />
          <Stat label="Avg pain drop" value={insights.avg_pain_drop !== null ? `${insights.avg_pain_drop} / 10` : "—"} icon={<TrendingDown className="w-4 h-4" />} highlight />
          <Stat label="Avg effectiveness" value={insights.avg_effectiveness !== null ? `${insights.avg_effectiveness} / 5` : "—"} icon={<Activity className="w-4 h-4" />} />
          <Stat label="Top muscle" value={insights.top_muscles[0]?.muscle?.replace(/_/g, " ") || "—"} icon={<Activity className="w-4 h-4" />} />
        </div>
      )}

      <div className="flex items-end justify-between mb-6">
        <div className="text-sm text-ink-500">Profile & history</div>
      </div>

      <ClinicalSnapshotEditor client={client} onSave={saveSnapshot} />

      <PatternSuggestionsPanel client={client} onClientUpdated={setClient} />

      <div className="mt-10 mb-2">
        <PhotoTimeline items={timeline} clientId={id} />
      </div>

      <div className="flex items-end justify-between mb-6 mt-12 flex-wrap gap-4">
        <h2 className="font-heading text-3xl font-light tracking-tight text-ink-900">Session history</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {completedSessions.length >= 1 && (
            <button
              type="button"
              onClick={() => setShareSessionId(completedSessions[0].id)}
              data-testid="share-with-client-btn"
              className="min-h-[40px] px-4 rounded-full bg-ink-900 hover:bg-ink-700 text-white text-sm font-medium inline-flex items-center gap-2 transition-all"
            >
              <Share2 className="w-4 h-4" /> Share with client
            </button>
          )}
          {completedSessions.length >= 1 && (
            <Link
              to={`/clients/${id}/body-chart`}
              data-testid="open-body-chart-btn"
              className="min-h-[40px] px-4 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white text-sm font-medium inline-flex items-center gap-2 transition-all"
            >
              <Flame className="w-4 h-4" /> Body chart history
            </Link>
          )}
          {completedSessions.length >= 1 && (
            <Link
              to={`/clients/${id}/timeline`}
              data-testid="open-timeline-btn"
              className="min-h-[40px] px-4 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-sm font-medium inline-flex items-center gap-2 transition-all"
            >
              <Activity className="w-4 h-4" /> View timeline
            </Link>
          )}
          {completedSessions.length >= 2 && (
            <Link
              to={`/clients/${id}/compare`}
              data-testid="compare-sessions-btn"
              className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm text-ink-700 inline-flex items-center gap-2 transition-all"
            >
              Compare sessions
            </Link>
          )}
          {completedSessions.length >= 1 && (
            <button
              type="button"
              onClick={() => setCsvOpen(true)}
              data-testid="export-csv-btn"
              className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm text-ink-700 inline-flex items-center gap-2 transition-all"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          )}
          <div className="text-sm text-ink-500">
            {completedSessions.length} completed · {activeDrafts.length} draft{activeDrafts.length === 1 ? "" : "s"}
            {archivedDrafts.length > 0 && ` · ${archivedDrafts.length} archived`}
          </div>
        </div>
      </div>

      {/* Unfinished session banners — persistent until completed or discarded */}
      {unfinishedDrafts.length > 0 && (
        <div className="space-y-3 mb-6" data-testid="unfinished-session-banners">
          {unfinishedDrafts.map((s) => {
            const critical = s.tier === "critical";
            const overdue = s.tier === "overdue" || critical;
            return (
              <div
                key={s.id}
                data-testid={`unfinished-banner-${s.id}`}
                className={`rounded-3xl border p-5 ${
                  critical ? "bg-red-50 border-red-200" : overdue ? "bg-orange-50 border-orange-200" : "bg-amber-50 border-amber-200"
                }`}
              >
                <div className="flex items-start gap-3 mb-4">
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    critical ? "bg-red-100 text-red-700" : overdue ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"
                  }`}>
                    <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <div>
                    <div className="font-medium text-ink-900 flex items-center gap-2 flex-wrap">
                      Unfinished Session
                      {overdue && (
                        <span className={`text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full ${critical ? "bg-red-100 text-red-800" : "bg-orange-100 text-orange-800"}`}>
                          {critical ? `Overdue ${s.ageDays} days` : "Overdue"}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-ink-700 mt-0.5">
                      The session from {new Date(s.created_at).toLocaleString([], { month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })} has not been completed or saved.
                      {" "}Last draft update {timeAgo(s.updated_at || s.created_at)}.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pl-13">
                  <Link
                    to={`/clients/${id}/new-session?sid=${s.id}`}
                    data-testid={`unfinished-resume-${s.id}`}
                    className="min-h-[40px] px-4 rounded-full bg-ink-900 text-white text-sm font-medium inline-flex items-center transition-all hover:bg-ink-900/90"
                  >
                    Resume Session
                  </Link>
                  <Link
                    to={`/sessions/${s.id}`}
                    data-testid={`unfinished-review-${s.id}`}
                    className="min-h-[40px] px-4 rounded-full bg-white border border-paper-200 hover:bg-paper-100 text-ink-700 text-sm inline-flex items-center transition-all"
                  >
                    Review Draft
                  </Link>
                  <button
                    onClick={() => markDraftComplete(s.id)}
                    data-testid={`unfinished-complete-${s.id}`}
                    className="min-h-[40px] px-4 rounded-full bg-white border border-paper-200 hover:bg-paper-100 text-ink-700 text-sm transition-all"
                  >
                    Mark Complete
                  </button>
                  <button
                    onClick={() => discardDraft(s.id)}
                    data-testid={`unfinished-discard-${s.id}`}
                    className="min-h-[40px] px-4 rounded-full bg-white border border-paper-200 hover:bg-paper-100 hover:text-destructive text-ink-500 text-sm transition-all"
                  >
                    Discard Draft
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Iter Y — Expanded "What Changed Since Last Visit" full card.
          Shown above sessions list when there are ≥2 completed sessions. */}
      {completedSessions.length >= 2 && (
        <div className="mb-6">
          <WhatChangedCard clientId={id} variant="full" />
        </div>
      )}

      {/* Session filter tabs */}
      {(completedSessions.length > 0 || activeDrafts.length > 0) && (
        <div className="flex items-center gap-1 bg-paper-100 rounded-full p-1 w-fit mb-5" data-testid="session-filter-tabs">
          {[
            { key: "all", label: "All" },
            { key: "unfinished", label: `Unfinished${activeDrafts.length ? ` (${activeDrafts.length})` : ""}` },
            { key: "completed", label: "Completed" },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`session-filter-${t.key}`}
              onClick={() => setSessionFilter(t.key)}
              className={`min-h-[38px] px-4 rounded-full text-xs font-bold uppercase tracking-[0.1em] transition-colors ${
                sessionFilter === t.key ? "bg-ink-900 text-white" : "text-ink-500 hover:text-ink-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Active drafts first — most actionable */}
      {sessionFilter !== "completed" && activeDrafts.length > 0 && (
        <div className="space-y-3 mb-6" data-testid="active-drafts-list">
          {activeDrafts.map((s) => {
            const daysLeft = daysUntilArchive(s);
            const expiringSoon = daysLeft != null && daysLeft <= 7;
            return (
              <Link
                key={s.id}
                to={`/clients/${id}/new-session?sid=${s.id}`}
                data-testid={`session-row-${s.id}`}
                className="block rounded-2xl p-5 transition-all border bg-terracotta-500/5 border-terracotta-500/30 hover:border-terracotta-500/50 hover:shadow-md"
              >
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-terracotta-500/10 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-terracotta-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-ink-900">
                          {new Date(s.created_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                        </div>
                        <span className="px-2 py-0.5 rounded-full bg-terracotta-500 text-white text-[10px] uppercase tracking-[0.14em] font-medium" data-testid={`draft-badge-${s.id}`}>
                          Draft — resume
                        </span>
                        {expiringSoon && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] uppercase tracking-[0.14em] font-medium" data-testid={`expires-soon-${s.id}`}>
                            <AlertTriangle className="w-3 h-3" /> Expires in {Math.max(0, daysLeft)} day{daysLeft === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-ink-500">
                        {s.worked_areas?.length || 0} areas worked
                        {" · "}
                        <span data-testid={`draft-saved-status-${s.id}`}>Draft saved {timeAgo(s.updated_at || s.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Completed sessions */}
      {completedSessions.length === 0 && activeDrafts.length === 0 ? (
        <div className="bg-white border border-paper-200 rounded-3xl p-12 text-center" data-testid="no-sessions-empty">
          <div className="font-heading text-xl text-ink-900 mb-2">No sessions yet</div>
          <p className="text-ink-700 mb-6">Capture your first posture analysis to start tracking outcomes.</p>
          <Link
            to={`/clients/${id}/new-session`}
            className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 transition-all"
          >
            <Plus className="w-4 h-4" /> Start a session
          </Link>
        </div>
      ) : sessionFilter === "unfinished" ? (
        activeDrafts.length === 0 && (
          <div className="bg-white border border-paper-200 rounded-3xl p-10 text-center text-ink-700" data-testid="no-unfinished-sessions">
            No unfinished sessions — everything is saved.
          </div>
        )
      ) : (
        <div className="space-y-3">
          {completedSessions.map((s) => {
            const drop = (s.outcome?.pain_before != null && s.outcome?.pain_after != null)
              ? s.outcome.pain_before - s.outcome.pain_after : null;
            const hasPhotos = !!(s.front_image || s.side_image || s.back_image);
            return (
              <Link
                key={s.id}
                to={`/sessions/${s.id}`}
                data-testid={`session-row-${s.id}`}
                className="block rounded-2xl p-5 transition-all border bg-white border-paper-200 hover:border-sage-500/40 hover:shadow-md"
              >
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-paper-100 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-ink-700" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="font-medium text-ink-900 flex items-center gap-2 flex-wrap">
                        {new Date(s.created_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                        {s.imported_from_history && (
                          <span
                            data-testid={`session-imported-badge-${s.id}`}
                            className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold"
                            title={s.imported_label || "Imported from historical notes"}
                          >
                            Imported
                          </span>
                        )}
                        {s.imported_from_history && !hasPhotos && (
                          <span
                            data-testid={`session-needs-photos-${s.id}`}
                            className="text-[10px] uppercase tracking-wider bg-paper-100 text-ink-700 px-2 py-0.5 rounded-full font-medium"
                          >
                            Add photos →
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-ink-500">
                        {s.duration_minutes ? `${s.duration_minutes} min · ` : ""}
                        {s.worked_areas?.length || 0} areas worked
                        {" · "}
                        <span data-testid={`completed-status-${s.id}`}>
                          Completed {new Date(s.updated_at || s.created_at).toLocaleString([], { month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {s.outcome?.pain_before != null && (
                      <Pill label={`Pain ${s.outcome.pain_before} → ${s.outcome.pain_after ?? "?"}`} tone={drop != null && drop > 0 ? "success" : "muted"} />
                    )}
                    {s.outcome?.effectiveness && (
                      <Pill label={`★ ${s.outcome.effectiveness}/5`} tone="accent" />
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Archived drafts */}
      {archivedDrafts.length > 0 && (
        <div className="mt-10" data-testid="archived-drafts-section">
          <button
            onClick={() => setShowArchived((v) => !v)}
            className="text-sm text-ink-500 hover:text-ink-900 inline-flex items-center gap-2 mb-3"
            data-testid="toggle-archived"
          >
            {showArchived ? "▾" : "▸"} Archived drafts ({archivedDrafts.length})
          </button>
          {showArchived && (
            <div className="space-y-2">
              {archivedDrafts.map((s) => (
                <div key={s.id} className="bg-paper-50 border border-paper-200 rounded-xl px-4 py-3 flex items-center justify-between flex-wrap gap-3" data-testid={`archived-row-${s.id}`}>
                  <div className="text-sm">
                    <div className="text-ink-900">
                      {new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      <span className="text-ink-500"> · {s.worked_areas?.length || 0} areas</span>
                    </div>
                    <div className="text-xs text-ink-500">archived {new Date(s.archived_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                  </div>
                  <button
                    onClick={() => restoreSession(s.id)}
                    className="min-h-[36px] px-3 rounded-full border border-paper-200 hover:bg-white text-xs text-ink-700 inline-flex items-center gap-1.5"
                    data-testid={`restore-btn-${s.id}`}
                  >
                    <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Email deliveries timeline (Sprint 2) */}
      {deliveries.length > 0 && (
        <div className="mt-12" data-testid="email-deliveries-section">
          <h2 className="font-heading text-2xl font-medium text-ink-900 mb-1">Delivered emails</h2>
          <p className="text-sm text-ink-500 mb-4">Reports sent to {client?.name || "the client"}. {deliveries.length} on record.</p>
          <div className="space-y-2">
            {deliveries.map((d) => (
              <div
                key={d.id}
                data-testid={`email-delivery-row-${d.id}`}
                className="bg-white border border-paper-200 rounded-2xl px-4 py-3 flex items-center justify-between flex-wrap gap-3"
              >
                <div className="text-sm min-w-0">
                  <div className="font-medium text-ink-900 truncate">
                    {d.kind === "compare_report" ? "Progress comparison" : "Session report"}
                    <span className="text-ink-500"> → {d.recipient_email}</span>
                  </div>
                  <div className="text-xs text-ink-500 mt-0.5">
                    {new Date(d.sent_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                    {d.pdf_filename && <span> · {d.pdf_filename}</span>}
                  </div>
                </div>
                <span
                  data-status={d.status}
                  className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${
                    d.status === "sent" ? "bg-sage-100 text-sage-800" : "bg-rose-100 text-rose-800"
                  }`}
                >
                  {d.status === "sent" ? "Sent" : "Failed"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Iter Y — Share with client dialog (Client Progress Portal) */}
      <PortalShareDialog
        sessionId={shareSessionId}
        open={!!shareSessionId}
        onClose={() => setShareSessionId(null)}
      />

      <CsvExportDialog
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        variant="client"
        clientId={id}
        clientName={client.name}
      />
    </Layout>
  );
}

function Stat({ label, value, icon, highlight }) {
  return (
    <div className={`rounded-2xl p-5 border ${highlight ? "bg-terracotta-500 border-terracotta-500 text-white" : "bg-white border-paper-200"}`}>
      <div className="flex items-center gap-2 mb-2 opacity-80">
        {icon}<span className="text-xs uppercase tracking-[0.14em]">{label}</span>
      </div>
      <div className={`font-heading text-2xl font-medium ${highlight ? "" : "text-ink-900"}`}>{value}</div>
    </div>
  );
}

function Pill({ label, tone = "muted" }) {
  const styles = {
    muted: "bg-paper-100 text-ink-700",
    success: "bg-sage-100 text-sage-700",
    accent: "bg-terracotta-500/10 text-terracotta-600",
  };
  return <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${styles[tone]}`}>{label}</span>;
}
